/**
 * Authentication middleware tags (Client-safe)
 *
 * This file contains only the RpcMiddleware.Tag declarations and Context.Tag declarations
 * that are needed by RPC definition files. These have no server-side dependencies and
 * can be safely imported by client packages.
 *
 * @since 1.0.0
 */
import { RpcMiddleware } from "@effect/rpc";
import type { Person } from "@fm/db/frontend";
import { Context } from "effect";

import { Unauthorized } from "./errors.ts";

// ============================================================================
// Context Tags
// ============================================================================

/**
 * CurrentActor context tag
 *
 * Provides information about the currently authenticated user making an RPC request.
 * This context is provided by the AuthMiddleware after successful token verification.
 *
 * @since 1.0.0
 */
export class CurrentActor extends Context.Tag("CurrentActor")<
  CurrentActor,
  {
    readonly clerkId: string;
    readonly sessionId: string;
    readonly person: typeof Person.Type;
  }
>() {}

// ============================================================================
// Middleware Tags
// ============================================================================

/**
 * Authentication middleware tag
 *
 * Verifies Clerk session tokens and provides CurrentActor context to RPC handlers.
 *
 * @since 1.0.0
 */
export class AuthMiddleware extends RpcMiddleware.Tag<AuthMiddleware>()(
  "AuthMiddleware",
  {
    provides: CurrentActor,
    failure: Unauthorized,
  },
) {}

/**
 * Special authentication middleware tag for ensureUser endpoint
 *
 * Verifies Clerk session tokens but does NOT fail if the user doesn't exist in the database.
 * This allows the ensureUser endpoint to create users that don't exist yet.
 *
 * @since 1.0.0
 */
export class EnsureUserAuthMiddleware extends RpcMiddleware.Tag<EnsureUserAuthMiddleware>()(
  "EnsureUserAuthMiddleware",
  {
    provides: CurrentActor,
    failure: Unauthorized,
  },
) {}
